import "./FileInput.scss"
import React, { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { generalStore } from '../../store';

const MAX_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB = 2,147,483,648 bytes
// Supported file formats
const allowedImageTypes = ['jpeg', 'png', 'tiff', 'webp', 'eps', 'rgb', 'rgba', 'sgi', 'gif'];
const allowedVideoTypes = ['avi', 'f4v', 'flv', 'm4v', 'mov', 'mp4', 'mpeg', 'mpg', 'wmv'];

export function FileInput({ 
    accept = "image/*, video/*" ,
    onAssetDroped,
    getFile
}) {
    const { t } = useTranslation();
    const { setAlert } = generalStore();

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [videoFile, setVideoFile] = useState<File | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleAssetUploadClick = () => {
        fileInputRef.current?.click();
    };

    const updateAsset = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];

        // Extract extension from file name
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        // File size validation
        if (file.size > MAX_BYTES) {
            setAlert({
                message: `${t('drope_zone_error_file_size')}${(file.size/1024/1024).toFixed(1)} MB (Max 2048 MB)`,
                type: 'warning',
                display: true
            });
            return;
        }

        // File type validation
        const isImage = fileExtension && allowedImageTypes.includes(fileExtension);
        const isVideo = fileExtension && allowedVideoTypes.includes(fileExtension);

        if (!isImage && !isVideo) {
            setAlert({
                message: t('drope_zone_error_file_type'),
                type: 'warning',
                display: true
            });
            return;
        }

        getFile(file)
        const fileType = file.type.split('/')[0]
        if(fileType === 'image'){
            setImageFile(file);
            setVideoFile(null);
        } else if(fileType === 'video'){
            setImageFile(null);
            setVideoFile(file);
        }
        onAssetDroped(true);
    }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    };

    const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
        const file = event.dataTransfer.files[0];

        // Extract extension from file name
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        // File size validation
        if (file.size > MAX_BYTES) {
            setAlert({
                message: `${t('drope_zone_error_file_size')}${(file.size/1024/1024).toFixed(1)} MB (Max 2048 MB)`,
                type: 'warning',
                display: true
            });
            return;
        }

        // File type validation
        const isImage = fileExtension && allowedImageTypes.includes(fileExtension);
        const isVideo = fileExtension && allowedVideoTypes.includes(fileExtension);

        if (!isImage && !isVideo) {
            setAlert({
                message: t('drope_zone_error_file_type'),
                type: 'warning',
                display: true
            });
            return;
        }

        getFile(file)
        const fileType = file.type.split('/')[0]
        if(fileType === 'image'){
            setImageFile(file);
            setVideoFile(null);
        } else if(fileType === 'video'){
            setImageFile(null);
            setVideoFile(file);
        }
        onAssetDroped(true);
    }
    };

    return (
    <div className="container">
        {!imageFile && !videoFile && (<div 
            className="drop-zone"
            onDragOver={handleDragOver}
            onDrop={handleFileDrop}
            onClick={handleAssetUploadClick}
        >
            <p>{t("drope_zone_1")}<br /><br />{t("drope_zone_2")}<br /><br />{t("drope_zone_3")}<br />{allowedImageTypes.join(', ')}<br />{allowedVideoTypes.join(', ')}</p>
            <br />
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                style={{ display: "none" }}
                onChange={updateAsset}
            />
        </div>)}
        {imageFile && (
            <img
                src={URL.createObjectURL(imageFile)}
                alt="Uploaded"
                className="uploadedImage"
            />
        )}
        {videoFile && (
            <video
                src={URL.createObjectURL(videoFile)}
                className="uploadedVideo"
            />
        )}
    </div>
    );
}